import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Spinner, Av, Modal, Select, Input } from '../ui.jsx';

const STAGES = ['New Lead','Contacted','Qualified','Estimate Sent','Booked','In Progress','Invoiced','Paid'];
const SCOL = { 'New Lead': G.cyan, 'Contacted': G.blue, 'Qualified': G.purple, 'Estimate Sent': G.yellow, 'Booked': G.green, 'In Progress': G.orange, 'Invoiced': G.pink, 'Paid': G.green };

export function Pipeline({ toast }) {
  const [allContacts, setAllContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  // Bulk selection — Set of contact IDs currently checked.
  const [selected, setSelected] = useState(() => new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  // Free-text search across name, phone, email, source — applied to every
  // Kanban column so the owner can spot a specific lead without leaving the
  // pipeline view.
  const [search, setSearch] = useState('');

  // Pipeline view = active-stage contacts that aren't hidden + match search.
  // The "hidden" flag lets you remove someone from the Kanban without marking
  // them Lost — useful for "not actively selling them right now but still
  // in the system."
  const matchesSearch = (c) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return [c.firstName, c.lastName, c.phone, c.email, c.source, c.service]
      .some(v => v && String(v).toLowerCase().includes(s));
  };
  const contacts = allContacts
    .filter(c => !c.pipelineHidden && !['Lost','Completed'].includes(c.stage))
    .filter(matchesSearch);
  const outsidePipeline = allContacts.filter(c => c.pipelineHidden || ['Lost', null, undefined, ''].includes(c.stage) || !STAGES.includes(c.stage));

  const load = () => api('/api/contacts').then(r => { setAllContacts(r); setSelected(new Set()); }).catch(e=>toast(e.message,'error')).finally(()=>setLoading(false));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('tradeops:tab-focus', onFocus);
    return () => window.removeEventListener('tradeops:tab-focus', onFocus);
  }, []);

  const moveStage = async (id, newStage) => {
    try { await api(`/api/contacts/${id}`, { method:'PATCH', body:{ stage: newStage } }); load(); toast(`Moved to ${newStage}`); }
    catch(e) { toast(e.message,'error'); }
  };

  // Bulk operations use /api/contacts/bulk so 100s of contacts in one request.
  const bulkSetStage = async (ids, stage) => {
    try {
      const r = await api('/api/contacts/bulk', { method:'POST', body:{ ids: Array.from(ids), action:'set_stage', value: stage }});
      toast(`✓ ${r.affected} moved to ${stage}`);
      load();
    } catch(e) { toast(e.message, 'error'); }
  };

  // Remove from pipeline = hide from the Kanban without marking Lost. Stage
  // stays intact so adding them back puts them in the same column.
  const removeSelected = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Hide ${selected.size} contact${selected.size===1?'':'s'} from the pipeline?\n\nThey stay in the system with their current stage — you can add them back anytime.`)) return;
    try {
      const r = await api('/api/contacts/bulk', { method:'POST', body:{ ids: Array.from(selected), action:'hide_from_pipeline' }});
      toast(`✓ ${r.affected} hidden from pipeline`);
      load();
    } catch(e) { toast(e.message, 'error'); }
  };

  // Hard "mark Lost" option for when a deal really is dead.
  const markLostSelected = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Mark ${selected.size} contact${selected.size===1?'':'s'} as Lost?\n\nThis sets stage='Lost' and removes them from the pipeline. Different from "hide" which preserves the original stage.`)) return;
    await bulkSetStage(selected, 'Lost');
  };

  const moveSelectedTo = async (stage) => {
    if (selected.size === 0) return;
    await bulkSetStage(selected, stage);
  };

  const toggleSelect = (id, e) => {
    e?.stopPropagation();
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };

  const onDragStart = (c, e) => { setDrag(c); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (stage, e) => { e.preventDefault(); setOver(stage); };
  const onDrop = (stage, e) => {
    e.preventDefault();
    if (drag && drag.stage !== stage) moveStage(drag.id, stage);
    setDrag(null); setOver(null);
  };

  const stageStats = STAGES.map(s => {
    const items = contacts.filter(c => c.stage === s);
    return { stage: s, count: items.length, value: items.reduce((sum,c)=>sum + (c.value||0), 0), items };
  });

  if (loading) return <div style={{ display:'flex', justifyContent:'center', padding:80 }}><Spinner sz={32}/></div>;

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, gap:14, flexWrap:'wrap' }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Pipeline</h1>
      <Input value={search} onChange={e=>setSearch(e.target.value)}
        placeholder="Search name, phone, email, source..." style={{ maxWidth:340, flex:'1 1 240px' }}/>
    </div>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, gap:14, flexWrap:'wrap' }}>
      <div/>
      <div style={{ display:'flex', gap:14, fontSize:13, color:G.textMuted, alignItems:'center' }}>
        <span>{contacts.length} active deals</span>
        <span>·</span>
        <span>Total: <strong style={{ color:G.green }}>{fmtUsd(contacts.reduce((s,c)=>s+(c.value||0),0))}</strong></span>
        <Btn sz="sm" v="ghost" onClick={()=>{
          // Select all = every contact card currently rendered (every stage, every column)
          if (selected.size === contacts.length && contacts.length > 0) setSelected(new Set());
          else setSelected(new Set(contacts.map(c => c.id)));
        }}>{selected.size === contacts.length && contacts.length > 0 ? '☑ Deselect all' : `☐ Select all (${contacts.length})`}</Btn>
        <Btn sz="sm" v="primary" onClick={()=>setShowAddModal(true)}>➕ Add to Pipeline</Btn>
      </div>
    </div>

    {/* Bulk action bar — only appears when 1+ cards selected */}
    {selected.size > 0 && <div style={{ ...styles.card, padding:'10px 14px', marginBottom:14, borderColor:G.purple+'66', background:G.purpleDim, display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
      <strong>{selected.size} selected</strong>
      <span style={{ color:G.textMuted, fontSize:12 }}>Move to:</span>
      <Select onChange={e => { const s = e.target.value; e.target.value=''; if (s) moveSelectedTo(s); }} style={{ width:160 }}>
        <option value="">— pick stage —</option>
        {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
      </Select>
      <Btn sz="sm" v="warn" onClick={removeSelected} title="Removes from Kanban but keeps stage + contact intact">👁‍🗨 Hide from pipeline</Btn>
      <Btn sz="sm" v="danger" onClick={markLostSelected} title="Marks deal as Lost (real lost-deal close)">🪦 Mark Lost</Btn>
      <Btn sz="sm" v="ghost" onClick={()=>setSelected(new Set())}>Clear</Btn>
    </div>}

    <div style={{ display:'flex', gap:10, overflowX:'auto', paddingBottom:14 }}>
      {stageStats.map(({ stage, count, value, items }) => (
        <div key={stage}
          onDragOver={e => onDragOver(stage, e)}
          onDrop={e => onDrop(stage, e)}
          style={{ minWidth:280, flex:'1 1 280px',
            background: over === stage ? G.purpleDim : 'transparent',
            border: over === stage ? `1px dashed ${G.purple}` : `1px dashed transparent`,
            borderRadius:10, padding:8, transition:'background .15s' }}>
          <div style={{ marginBottom:10, padding:'0 4px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
              <div style={{ fontSize:12, fontWeight:800, textTransform:'uppercase', letterSpacing:'.05em', color:SCOL[stage] }}>{stage}</div>
              <span style={{ fontSize:11, color:G.textMuted, background:G.input, padding:'2px 8px', borderRadius:10 }}>{count}</span>
            </div>
            <div style={{ fontSize:13, color:G.textMuted }}>{fmtUsd(value)}</div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {items.map(c => {
              const isSelected = selected.has(c.id);
              return <div key={c.id} draggable onDragStart={e => onDragStart(c, e)}
                onClick={() => {
                  try { window.dispatchEvent(new CustomEvent('tradeops:open-contact', { detail: { id: c.id }})); } catch {}
                }}
                style={{ ...styles.card, padding:12, cursor:'pointer',
                  borderColor: isSelected ? G.purple : SCOL[stage]+'33',
                  background: isSelected ? G.purpleDim : G.card,
                  opacity: drag?.id === c.id ? 0.4 : 1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                  <input type="checkbox" checked={isSelected} onChange={e=>toggleSelect(c.id, e)} onClick={e=>e.stopPropagation()} style={{ cursor:'pointer' }}/>
                  <Av name={`${c.firstName} ${c.lastName||''}`} sz={28}/>
                  <strong style={{ fontSize:13 }}>{c.firstName} {c.lastName}</strong>
                </div>
                <div style={{ fontSize:12, color:G.textMuted }}>{c.service || '—'}</div>
                {c.value > 0 && <div style={{ fontSize:13, fontWeight:700, color:G.green, marginTop:4 }}>{fmtUsd(c.value)}</div>}
                <div style={{ display:'flex', gap:4, marginTop:6, flexWrap:'wrap' }}>
                  <Badge c={G.textMuted} bg={G.input}>{c.source}</Badge>
                  {c.aiActive && <Badge c={G.purple} bg={G.purpleDim}>🤖 AI</Badge>}
                </div>
              </div>;
            })}
            {items.length === 0 && <div style={{ padding:20, textAlign:'center', fontSize:12, color:G.textDim, border:`1px dashed ${G.border}`, borderRadius:8 }}>Drop here</div>}
          </div>
        </div>
      ))}
    </div>

    <div style={{ ...styles.card, marginTop:14, padding:14, fontSize:13, color:G.textMuted, lineHeight:1.5 }}>
      💡 Drag deals across stages to update them — each move triggers the matching workflow. Use checkboxes for bulk move / remove.
    </div>

    {showAddModal && <AddToPipelineModal
      candidates={outsidePipeline}
      onClose={()=>setShowAddModal(false)}
      onAdd={async (ids, stage) => {
        if (!ids.length) return;
        try {
          // Clear any pipeline_hidden flag, then set their target stage. Two
          // calls because the bulk endpoint takes one action at a time.
          await api('/api/contacts/bulk', { method:'POST', body:{ ids, action:'show_in_pipeline' }});
          const r = await api('/api/contacts/bulk', { method:'POST', body:{ ids, action:'set_stage', value: stage }});
          toast(`✓ ${r.affected} added to ${stage}`);
          load();
        } catch(e) { toast(e.message, 'error'); }
        setShowAddModal(false);
      }}
    />}
  </div>;
}

// Modal: shows contacts NOT currently in the active pipeline (Lost or stageless)
// + lets the user multi-select them and pick a target stage to add them to.
function AddToPipelineModal({ candidates, onClose, onAdd }) {
  const [q, setQ] = useState('');
  const [chosen, setChosen] = useState(() => new Set());
  const [stage, setStage] = useState('New Lead');
  const filtered = candidates.filter(c => {
    if (!q.trim()) return true;
    const term = q.toLowerCase();
    return [c.firstName, c.lastName, c.phone, c.email, c.source].filter(Boolean).some(v => String(v).toLowerCase().includes(term));
  });
  const toggle = (id) => setChosen(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAll = () => {
    if (chosen.size === filtered.length) setChosen(new Set());
    else setChosen(new Set(filtered.map(c => c.id)));
  };
  return <Modal open={true} width={720} onClose={onClose}>
    <h3 style={{ margin:'0 0 8px 0' }}>Add contacts to pipeline</h3>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:12 }}>
      Pick contacts currently outside the pipeline (Lost, hidden, or no stage) and move them into a pipeline stage.
    </div>
    <div style={{ display:'flex', gap:10, marginBottom:12, alignItems:'center' }}>
      <Input placeholder="Search by name, phone, email, source…" value={q} onChange={e=>setQ(e.target.value)} style={{ flex:1 }}/>
      <Select value={stage} onChange={e=>setStage(e.target.value)} style={{ width:180 }}>
        {STAGES.map(s => <option key={s} value={s}>Add to: {s}</option>)}
      </Select>
    </div>
    <div style={{ maxHeight:'50vh', overflowY:'auto', border:`1px solid ${G.cardBorder}`, borderRadius:8 }}>
      <div style={{ padding:'8px 12px', borderBottom:`1px solid ${G.cardBorder}`, background:G.input, display:'flex', alignItems:'center', gap:8 }}>
        <input type="checkbox" checked={filtered.length > 0 && chosen.size === filtered.length} onChange={toggleAll}/>
        <span style={{ fontSize:12, color:G.textMuted }}>
          {chosen.size > 0 ? `${chosen.size} of ${filtered.length} selected` : `Select all ${filtered.length}`}
        </span>
      </div>
      {filtered.map(c => (
        <label key={c.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', borderBottom:`1px solid ${G.cardBorder}`, cursor:'pointer' }}>
          <input type="checkbox" checked={chosen.has(c.id)} onChange={()=>toggle(c.id)}/>
          <Av name={`${c.firstName} ${c.lastName||''}`} sz={24}/>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:600, fontSize:13 }}>{c.firstName} {c.lastName}</div>
            <div style={{ fontSize:11, color:G.textMuted }}>
              {[c.phone, c.email, c.source].filter(Boolean).join(' · ')} {c.stage ? `· stage: ${c.stage}` : '· no stage'} {c.pipelineHidden ? '· 👁‍🗨 hidden' : ''}
            </div>
          </div>
        </label>
      ))}
      {filtered.length === 0 && <div style={{ padding:30, textAlign:'center', color:G.textMuted, fontSize:13 }}>
        {candidates.length === 0 ? 'All your contacts are already in the pipeline. New contacts get added automatically.' : 'No matches for that search.'}
      </div>}
    </div>
    <div style={{ display:'flex', justifyContent:'space-between', marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" disabled={chosen.size === 0} onClick={()=>onAdd(Array.from(chosen), stage)}>
        Add {chosen.size > 0 ? chosen.size : ''} to {stage}
      </Btn>
    </div>
  </Modal>;
}
